import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Star,
  Quote,
  ChevronLeft,
  ChevronRight,
  Play,
  Pause,
  User,
  Calendar,
  MapPin,
  Heart,
  Share2,
  Filter,
  Search,
  Video,
  MessageSquare,
  Award,
  X,
  Image as ImageIcon,
} from 'lucide-react'

interface Testimonial {
  id: string
  name: string
  role: string
  company: string
  location: string
  date: string
  rating: number
  review: string
  avatar: string
  images?: string[]
  videoUrl?: string
  category: string
  featured: boolean
  verified: boolean
}

const Testimonials = () => {
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [currentTestimonial, setCurrentTestimonial] = useState(0)
  const [isAutoPlay, setIsAutoPlay] = useState(true)
  const [selectedTestimonial, setSelectedTestimonial] = useState<Testimonial | null>(null)

  const categories = [
    { id: 'all', name: 'All Reviews', count: 127 },
    { id: 'weddings', name: 'Weddings', count: 45 },
    { id: 'corporate', name: 'Corporate', count: 38 },
    { id: 'events', name: 'Events', count: 28 },
    { id: 'portraits', name: 'Portraits', count: 16 },
  ]

  // Sample testimonials data
  const testimonials: Testimonial[] = [
    {
      id: '1',
      name: 'Sarah Johnson',
      role: 'Bride',
      company: 'Johnson-Smith Wedding',
      location: 'Mumbai, India',
      date: '2024-01-15',
      rating: 5,
      review: 'Parth Video exceeded all our expectations! Their team captured every precious moment of our wedding day with such artistry and professionalism. The final video brought tears to our eyes - it was like reliving our special day all over again. Highly recommended!',
      avatar: '/testimonials/sarah-avatar.jpg',
      images: ['/testimonials/sarah-1.jpg', '/testimonials/sarah-2.jpg'],
      category: 'weddings',
      featured: true,
      verified: true,
    },
    {
      id: '2',
      name: 'Rajesh Patel',
      role: 'CEO',
      company: 'TechCorp Industries',
      location: 'Delhi, India',
      date: '2024-01-20',
      rating: 5,
      review: 'Outstanding work on our product launch event! The team was incredibly professional and delivered a stunning video that perfectly captured the essence of our brand. The attention to detail and creative vision was remarkable.',
      avatar: '/testimonials/rajesh-avatar.jpg',
      videoUrl: '/testimonials/rajesh-video.mp4',
      category: 'corporate',
      featured: true,
      verified: true,
    },
    {
      id: '3',
      name: 'Priya Sharma',
      role: 'Event Organizer',
      company: 'Celebrations Plus',
      location: 'Bangalore, India',
      date: '2024-01-25',
      rating: 5,
      review: 'We have worked with Parth Video on multiple events and they never disappoint. Their creativity, punctuality, and quality of work is consistently excellent. They understand our vision and bring it to life beautifully.',
      avatar: '/testimonials/priya-avatar.jpg',
      category: 'events',
      featured: false,
      verified: true,
    },
    {
      id: '4',
      name: 'Michael Chen',
      role: 'Marketing Director',
      company: 'Global Solutions Ltd',
      location: 'Chennai, India',
      date: '2024-02-01',
      rating: 5,
      review: 'Exceptional service from start to finish. The team was professional, creative, and delivered beyond our expectations. The corporate video they created has significantly boosted our brand image.',
      avatar: '/testimonials/michael-avatar.jpg',
      category: 'corporate',
      featured: false,
      verified: true,
    },
    {
      id: '5',
      name: 'Anita Gupta',
      role: 'Mother of the Bride',
      company: 'Gupta Family',
      location: 'Pune, India',
      date: '2024-02-05',
      rating: 5,
      review: 'The team captured our daughter\'s wedding so beautifully. Every emotion, every smile, every tear of joy was preserved perfectly. We will treasure these memories forever. Thank you for making our special day even more memorable!',
      avatar: '/testimonials/anita-avatar.jpg',
      images: ['/testimonials/anita-1.jpg'],
      category: 'weddings',
      featured: true,
      verified: true,
    },
    {
      id: '6',
      name: 'David Wilson',
      role: 'Entrepreneur',
      company: 'Wilson Enterprises',
      location: 'Hyderabad, India',
      date: '2024-02-10',
      rating: 4,
      review: 'Great experience working with Parth Video. They were professional, timely, and produced high-quality content for our business portraits. The team was easy to work with and understood our requirements perfectly.',
      avatar: '/testimonials/david-avatar.jpg',
      category: 'portraits',
      featured: false,
      verified: true,
    },
  ]

  const filteredTestimonials = testimonials.filter(testimonial => {
    const matchesCategory = selectedCategory === 'all' || testimonial.category === selectedCategory
    const matchesSearch = testimonial.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         testimonial.review.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         testimonial.company.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesCategory && matchesSearch
  })

  const featuredTestimonials = testimonials.filter(t => t.featured)

  // Auto-play carousel
  useEffect(() => {
    if (isAutoPlay && featuredTestimonials.length > 0) {
      const interval = setInterval(() => {
        setCurrentTestimonial((prev) => 
          prev === featuredTestimonials.length - 1 ? 0 : prev + 1
        )
      }, 5000)
      return () => clearInterval(interval)
    }
  }, [isAutoPlay, featuredTestimonials.length])

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => 
      prev === featuredTestimonials.length - 1 ? 0 : prev + 1
    )
  }

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => 
      prev === 0 ? featuredTestimonials.length - 1 : prev - 1
    )
  }

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-5 h-5 ${
          i < rating ? 'text-yellow-400 fill-current' : 'text-gray-400'
        }`}
      />
    ))
  }

  const averageRating = testimonials.reduce((acc, t) => acc + t.rating, 0) / testimonials.length
  const totalReviews = testimonials.length
  const fiveStarCount = testimonials.filter(t => t.rating === 5).length
  const fiveStarPercentage = (fiveStarCount / totalReviews) * 100

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen pt-24"
    >
      {/* Hero Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Client <span className="gradient-text">Testimonials</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/70 max-w-4xl mx-auto leading-relaxed mb-8">
              Hear what our clients say about their experience with Parth Video
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="glass-card text-center"
              >
                <div className="text-3xl font-bold text-white mb-2">{averageRating.toFixed(1)}</div>
                <div className="flex justify-center mb-2">
                  {renderStars(Math.round(averageRating))}
                </div>
                <div className="text-white/60 text-sm">Average Rating</div>
              </motion.div>
              
              <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="glass-card text-center"
              >
                <div className="text-3xl font-bold text-white mb-2">{totalReviews}+</div>
                <div className="text-white/60 text-sm">Happy Clients</div>
              </motion.div>
              
              <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="glass-card text-center"
              >
                <div className="text-3xl font-bold text-white mb-2">{fiveStarPercentage.toFixed(0)}%</div>
                <div className="text-white/60 text-sm">5-Star Reviews</div>
              </motion.div>
              
              <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="glass-card text-center"
              >
                <div className="text-3xl font-bold text-white mb-2">100%</div>
                <div className="text-white/60 text-sm">Satisfaction Rate</div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Testimonials Carousel */}
      <section className="section-padding bg-black/20">
        <div className="container-custom">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Featured <span className="gradient-text">Reviews</span>
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              Highlighted testimonials from our most satisfied clients
            </p>
          </motion.div>

          <div className="relative max-w-4xl mx-auto">
            <AnimatePresence mode="wait">
              {featuredTestimonials.length > 0 && (
                <motion.div
                  key={currentTestimonial}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.5 }}
                  className="glass-card text-center relative"
                >
                  <Quote className="w-12 h-12 text-primary-400 mx-auto mb-6" />
                  
                  <blockquote className="text-xl md:text-2xl text-white/90 leading-relaxed mb-8 italic">
                    "{featuredTestimonials[currentTestimonial]?.review}"
                  </blockquote>
                  
                  <div className="flex items-center justify-center space-x-4 mb-6">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 flex items-center justify-center">
                      <User className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-left">
                      <div className="text-white font-semibold text-lg">
                        {featuredTestimonials[currentTestimonial]?.name}
                      </div>
                      <div className="text-white/70">
                        {featuredTestimonials[currentTestimonial]?.role} at {featuredTestimonials[currentTestimonial]?.company}
                      </div>
                      <div className="flex items-center space-x-2 mt-1">
                        <MapPin className="w-4 h-4 text-white/60" />
                        <span className="text-white/60 text-sm">{featuredTestimonials[currentTestimonial]?.location}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-center mb-6">
                    {renderStars(featuredTestimonials[currentTestimonial]?.rating || 0)}
                  </div>
                  
                  {featuredTestimonials[currentTestimonial]?.verified && (
                    <div className="flex items-center justify-center space-x-2 text-green-400">
                      <Award className="w-5 h-5" />
                      <span className="text-sm">Verified Review</span>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Navigation */}
            <button
              onClick={prevTestimonial}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 p-3 rounded-full glass hover:bg-white/20 transition-all duration-300"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <button
              onClick={nextTestimonial}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 p-3 rounded-full glass hover:bg-white/20 transition-all duration-300"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>

            {/* Auto-play control */}
            <button
              onClick={() => setIsAutoPlay(!isAutoPlay)}
              className="absolute bottom-4 right-4 p-2 rounded-full glass hover:bg-white/20 transition-all duration-300"
            >
              {isAutoPlay ? (
                <Pause className="w-5 h-5 text-white" />
              ) : (
                <Play className="w-5 h-5 text-white" />
              )}
            </button>

            {/* Dots indicator */}
            <div className="flex justify-center space-x-2 mt-8">
              {featuredTestimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentTestimonial
                      ? 'bg-primary-500 scale-125'
                      : 'bg-white/30 hover:bg-white/50'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* All Testimonials */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              All <span className="gradient-text">Reviews</span>
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              Browse through all our client testimonials and experiences
            </p>
          </motion.div>

          {/* Filters */}
          <div className="glass-card mb-8">
            <div className="flex flex-col lg:flex-row items-center justify-between space-y-6 lg:space-y-0">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/60" />
                <input
                  type="text"
                  placeholder="Search reviews..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="glass-input pl-10 w-full"
                />
              </div>

              {/* Category Filter */}
              <div className="flex items-center space-x-2">
                <Filter className="w-5 h-5 text-white/60" />
                <div className="flex space-x-2">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                        selectedCategory === category.id
                          ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white'
                          : 'glass hover:bg-white/20 text-white/80'
                      }`}
                    >
                      {category.name} ({category.count})
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Testimonials Grid */}
          <motion.div
            layout
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            <AnimatePresence>
              {filteredTestimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: index * 0.05 }}
                  className="glass-card hover-lift hover-glow group cursor-pointer"
                  onClick={() => setSelectedTestimonial(testimonial)}
                >
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 flex items-center justify-center">
                        <User className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <div className="text-white font-semibold">{testimonial.name}</div>
                        <div className="text-white/70 text-sm">{testimonial.role}</div>
                      </div>
                    </div>
                    
                    {testimonial.verified && (
                      <div className="p-1 rounded-full bg-green-500/20">
                        <Award className="w-4 h-4 text-green-400" />
                      </div>
                    )}
                  </div>

                  {/* Rating */}
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="flex">
                      {renderStars(testimonial.rating)}
                    </div>
                    <span className="text-white/60 text-sm">({testimonial.rating}.0)</span>
                  </div>

                  {/* Review */}
                  <blockquote className="text-white/80 leading-relaxed mb-4 line-clamp-4">
                    "{testimonial.review}"
                  </blockquote>

                  {/* Footer */}
                  <div className="flex items-center justify-between text-sm text-white/60">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(testimonial.date).toLocaleDateString()}</span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {testimonial.videoUrl && (
                        <Video className="w-4 h-4 text-blue-400" />
                      )}
                      {testimonial.images && testimonial.images.length > 0 && (
                        <span className="text-xs bg-white/20 px-2 py-1 rounded">
                          +{testimonial.images.length} photos
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Company */}
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <div className="text-white/70 text-sm">{testimonial.company}</div>
                    <div className="flex items-center space-x-1 text-white/60 text-xs">
                      <MapPin className="w-3 h-3" />
                      <span>{testimonial.location}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>

          {filteredTestimonials.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <MessageSquare className="w-16 h-16 text-white/40 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white/60 mb-2">
                No testimonials found
              </h3>
              <p className="text-white/40">
                Try adjusting your search or filter criteria.
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Detailed Testimonial Modal */}
      <AnimatePresence>
        {selectedTestimonial && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setSelectedTestimonial(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass-card max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 flex items-center justify-center">
                    <User className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <div className="text-xl font-semibold text-white">{selectedTestimonial.name}</div>
                    <div className="text-white/70">{selectedTestimonial.role} at {selectedTestimonial.company}</div>
                    <div className="flex items-center space-x-2 mt-1">
                      <MapPin className="w-4 h-4 text-white/60" />
                      <span className="text-white/60 text-sm">{selectedTestimonial.location}</span>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={() => setSelectedTestimonial(null)}
                  className="p-2 rounded-full hover:bg-white/20 transition-colors duration-300"
                >
                  <X className="w-5 h-5 text-white/70" />
                </button>
              </div>

              {/* Rating and Date */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                  <div className="flex">
                    {renderStars(selectedTestimonial.rating)}
                  </div>
                  <span className="text-white/60">({selectedTestimonial.rating}.0)</span>
                  {selectedTestimonial.verified && (
                    <div className="flex items-center space-x-1 text-green-400">
                      <Award className="w-4 h-4" />
                      <span className="text-sm">Verified</span>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center space-x-2 text-white/60">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date(selectedTestimonial.date).toLocaleDateString()}</span>
                </div>
              </div>

              {/* Review */}
              <blockquote className="text-white/90 leading-relaxed mb-6 text-lg">
                "{selectedTestimonial.review}"
              </blockquote>

              {/* Media */}
              {(selectedTestimonial.images || selectedTestimonial.videoUrl) && (
                <div className="mb-6">
                  <h4 className="text-white font-medium mb-3">Attached Media</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {selectedTestimonial.videoUrl && (
                      <div className="aspect-video bg-black rounded-lg flex items-center justify-center">
                        <Play className="w-8 h-8 text-white/60" />
                      </div>
                    )}
                    {selectedTestimonial.images?.map((_, index) => (
                      <div key={index} className="aspect-square bg-gradient-to-br from-primary-500/20 to-secondary-500/20 rounded-lg flex items-center justify-center">
                        <ImageIcon className="w-8 h-8 text-white/60" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <div className="flex items-center space-x-4">
                  <button className="flex items-center space-x-2 text-white/70 hover:text-white transition-colors duration-300">
                    <Heart className="w-5 h-5" />
                    <span>Helpful</span>
                  </button>
                  <button className="flex items-center space-x-2 text-white/70 hover:text-white transition-colors duration-300">
                    <Share2 className="w-5 h-5" />
                    <span>Share</span>
                  </button>
                </div>
                
                <div className="text-white/60 text-sm">
                  Category: {selectedTestimonial.category}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

export default Testimonials